import * as React from "react";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import { EmailInput } from "../../../ui/components/Form";
import type { OrganizationScopedRole } from "../../lib/api/users";
import * as userApi from "../../lib/api/users";
import { getApiErrorMessage } from "../../lib/api/api-error";
import { useI18n } from "../../lib/i18n";
import * as confirmStyles from "./OrganizationDetailPage.css";

const INVITE_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatRoleLabel(role: string): string {
  return role
    .replace(/^pro:/, "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isValidInviteEmail(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length > 0 && INVITE_EMAIL_PATTERN.test(trimmed);
}

export function InviteMemberModal({
  open,
  setOpen,
  orgId,
  orgName,
  token,
  roles,
  rolesLoading,
  onInvited,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  orgId: string;
  orgName: string;
  token: string | null;
  roles: OrganizationScopedRole[];
  rolesLoading: boolean;
  onInvited?: () => void;
}) {
  const { t, tx } = useI18n();
  const [email, setEmail] = React.useState("");
  const [roleId, setRoleId] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  /** When true, show inline email validation (set on blur, cleared while typing). */
  const [showEmailValidation, setShowEmailValidation] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setEmail("");
      setRoleId("");
      setError(null);
      setSuccess(null);
      setLoading(false);
      setShowEmailValidation(false);
      return;
    }
    if (!roleId && roles.length > 0) {
      const member =
        roles.find((r) => r.systemKey === "member" || r.name === "member" || r.name === "pro:member") ??
        roles[0];
      setRoleId(member.id);
    }
  }, [open, roles, roleId]);

  const trimmedEmail = email.trim();
  const emailValid = isValidInviteEmail(email);
  const emailFieldError =
    showEmailValidation && trimmedEmail.length > 0 && !emailValid
      ? "Please enter a valid email address (e.g. name@company.com)"
      : undefined;
  const canInvite =
    Boolean(token) && emailValid && Boolean(roleId) && roles.length > 0;

  const handleInvite = async () => {
    if (!token) {
      setError("Session expired. Please sign in again.");
      return;
    }
    if (!emailValid) {
      setShowEmailValidation(true);
      setError("Please enter a valid email address (e.g. name@company.com)");
      return;
    }
    if (!roleId) {
      setError("Select a role");
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await userApi.createOrganizationInvite(token, orgId, {
        email: trimmedEmail,
        roleId,
      });
      setSuccess(`Invitation sent to ${trimmedEmail}`);
      setEmail("");
      onInvited?.();
      setShowEmailValidation(false);
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to send invitation"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      minWidth="480px"
      maxWidth="520px"
      minHeight="auto"
    >
      <div className={confirmStyles.confirmModalBody}>
        <h3 className={confirmStyles.confirmModalTitle}>{t("pro.orgs.inviteMember")}</h3>
        <p className={confirmStyles.confirmModalText}>
          Send an invitation to join <strong>{orgName}</strong>. They will receive
          an email with a link to accept and join your organization.
        </p>

        {error && <Callout variant="error">{error}</Callout>}
        {success && <Callout variant="success">{success}</Callout>}

        <EmailInput
          label={t("pro.orgs.emailAddress")}
          placeholder="colleague@company.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setShowEmailValidation(false);
            if (error) setError(null);
          }}
          onBlur={() => setShowEmailValidation(true)}
          error={emailFieldError}
          autoComplete="email"
          disabled={loading}
        />

        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: 14, fontWeight: 500 }}>{tx("pro.orgs.role")}</span>
          <select
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            disabled={loading || rolesLoading || roles.length === 0}
            style={{
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px solid var(--color-border, #e5e7eb)",
              fontSize: 14,
            }}
          >
            {roles.length === 0 ? (
              <option value="">{t("pro.orgs.noRolesAvailable")}</option>
            ) : (
              roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.label?.trim() || formatRoleLabel(role.name)}
                </option>
              ))
            )}
          </select>
        </label>

        <div className={confirmStyles.confirmModalActions}>
          <Button
            color="neutral"
            variant="outline"
            size="md"
            disabled={loading}
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="solid"
            size="md"
            loading={loading}
            disabled={!canInvite}
            onClick={() => void handleInvite()}
          >
            Invite
          </Button>
        </div>
      </div>
    </Modal>
  );
}
